// src/components/chat/BuddyPanel.jsx — 1-to-1 Private Assistant with Tool Calling & Proposal Cards
import React, { useState, useEffect, useRef } from "react";
import { Send, Sparkles, Check, X, Trash2, Shield } from "lucide-react";
import { useChat } from "../../context/ChatContext";
import { useAuth } from "../../context/AuthContext";

const QUICK_CHIPS = [
  { label: "📅 Aaj ke follow-ups", prompt: "Aaj ke kaun se follow-ups pending hain?" },
  { label: "⚠️ Overdue leads", prompt: "Meri kaun si leads overdue ho rahi hain?" },
  { label: "📊 Meri performance", prompt: "Meri current month ki conversion aur sales stats batao." },
  { label: "🚗 Fresh stock search", prompt: "Stock mein kaun si fresh Japanese gariyan available hain?" },
  { label: "⏰ Reminder lagao", prompt: "Kal subah 10 baje client ko call karne ka reminder set karo." }
];

export default function BuddyPanel() {
  const { user } = useAuth();
  const {
    buddySettings,
    buddyMessages,
    buddyPendingActions,
    buddyLoading,
    sendBuddyMessage,
    confirmBuddyAction,
    rejectBuddyAction,
    clearBuddyHistory
  } = useChat();

  const [inputVal, setInputVal] = useState("");
  const scrollRef = useRef(null);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [buddyMessages, buddyPendingActions, buddyLoading]);

  const handleSend = (e) => {
    if (e) e.preventDefault();
    if (!inputVal.trim() || buddyLoading) return;
    const text = inputVal.trim();
    setInputVal("");
    sendBuddyMessage(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return "";
    const d = new Date(isoString);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const personaLabel = {
    kind: "Kind Assistant",
    tapori_lite: "Tapori Lite",
    english_chill: "English Chill"
  }[buddySettings?.persona || "kind"];

  return (
    <div className="buddy-panel">
      {/* ── 1. Hero Header ── */}
      <div className="buddy-hero-banner">
        <div className="buddy-hero-info">
          <div className="buddy-avatar-pulse">
            <Sparkles size={18} />
          </div>
          <div>
            <div className="buddy-hero-title">Buddy Assistant</div>
            <div className="buddy-hero-sub">
              1-to-1 Private Copilot • <span style={{ color: "var(--primary)" }}>{personaLabel}</span>
            </div>
          </div>
        </div>

        <button
          className="chat-icon-btn"
          title="Clear Conversation History"
          onClick={() => {
            if (window.confirm("Clear all Buddy chat history and proposal cards?")) {
              clearBuddyHistory();
            }
          }}
        >
          <Trash2 size={15} />
        </button>
      </div>

      {/* ── 2. Quick Action Chips ── */}
      <div className="buddy-chips-bar">
        {QUICK_CHIPS.map((chip, idx) => (
          <button
            key={idx}
            className="buddy-quick-chip"
            onClick={() => sendBuddyMessage(chip.prompt)}
            disabled={buddyLoading}
          >
            {chip.label}
          </button>
        ))}
      </div>

      {/* ── 3. Messages Stream ── */}
      <div className="chat-messages-scroll" ref={scrollRef}>
        {buddyMessages.length === 0 ? (
          <div style={{ textAlign: "center", color: "var(--muted)", margin: "auto", padding: "20px", maxWidth: "300px" }}>
            <div style={{ fontSize: "28px", marginBottom: "8px" }}>🤖✨</div>
            <div style={{ fontWeight: 600, color: "var(--text)", marginBottom: "4px" }}>Salam {user?.name}!</div>
            <p style={{ fontSize: "12px", lineHeight: 1.5, margin: 0 }}>
              Main aapka private AI assistant hoon. Apni leads, follow-ups, CIF calculation, ya reminder ke baray mein kuch bhi poochhein!
            </p>
          </div>
        ) : (
          buddyMessages.map((msg) => {
            const isUser = msg.role === "user";

            return (
              <div
                key={msg.id}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: isUser ? "flex-end" : "flex-start",
                  margin: "4px 0"
                }}
              >
                <div
                  style={{
                    maxWidth: "85%",
                    padding: "8px 12px",
                    borderRadius: isUser ? "14px 14px 2px 14px" : "14px 14px 14px 2px",
                    background: isUser ? "var(--primary)" : "var(--surface-2)",
                    color: isUser ? "#fff" : "var(--text)",
                    fontSize: "13px",
                    lineHeight: 1.45,
                    border: isUser ? "none" : "1px solid var(--border)",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    boxShadow: isUser ? "0 2px 8px rgba(10, 132, 255, 0.25)" : "none"
                  }}
                >
                  {msg.body}
                </div>
                <span style={{ fontSize: "10px", color: "var(--muted)", marginTop: "2px", padding: "0 4px" }}>
                  {formatTime(msg.created_at)}
                </span>
              </div>
            );
          })
        )}

        {/* ── 4. Interactive Proposal Cards ── */}
        {buddyPendingActions.map((action) => (
          <div key={action.id} className="buddy-proposal-card">
            <div className="proposal-head">
              <Shield size={13} /> Proposed Action: {action.type || action.action_type}
            </div>
            <div className="proposal-summary">
              {action.summary || "Please review and confirm this action."}
            </div>
            <div className="proposal-actions-row">
              <button
                className="proposal-confirm-btn"
                onClick={() => confirmBuddyAction(action.id)}
              >
                <Check size={14} /> Confirm
              </button>
              <button
                className="proposal-cancel-btn"
                onClick={() => rejectBuddyAction(action.id)}
              >
                <X size={14} /> Cancel
              </button>
            </div>
          </div>
        ))}

        {/* Typing / Loading indicator */}
        {buddyLoading && (
          <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "var(--muted)", padding: "6px 8px" }}>
            <Sparkles size={14} className="text-primary" /> Buddy is thinking...
          </div>
        )}
      </div>

      {/* ── 5. Message Input ── */}
      <div className="chat-composer">
        <form className="chat-composer-box" onSubmit={handleSend}>
          <textarea
            className="chat-textarea"
            placeholder="Ask Buddy (leads, follow-ups, reminders)..."
            rows={1}
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={buddyLoading}
          />

          <button
            type="submit"
            className="chat-send-btn"
            disabled={!inputVal.trim() || buddyLoading}
            title="Send"
          >
            <Send size={15} />
          </button>
        </form>
      </div>
    </div>
  );
}
